import { useState, useEffect, useCallback } from 'react';
import { Link, useParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { movies, categories, subCategories } from '@/mocks/movies';
import type { MovieItem } from '@/mocks/movies';

export default function Category() {
  const { slug } = useParams<{ slug: string }>();
  const currentSlug = slug || 'movie';
  const [activeSub, setActiveSub] = useState('全部');
  const [filtered, setFiltered] = useState<MovieItem[]>([]);

  const currentCategory = categories.find((c) => c.slug === currentSlug);
  const subs = subCategories[currentSlug] || [];
  const allSubs = ['全部', ...subs];

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [currentSlug, activeSub]);

  useEffect(() => {
    let result = movies.filter((m) => m.category === currentSlug);
    if (activeSub !== '全部') {
      result = result.filter((m) => m.subCategory === activeSub);
    }
    setFiltered(result);
    setActiveSub('全部');
  }, [currentSlug]);

  useEffect(() => {
    let result = movies.filter((m) => m.category === currentSlug);
    if (activeSub !== '全部') {
      result = result.filter((m) => m.subCategory === activeSub);
    }
    setFiltered(result);
  }, [activeSub, currentSlug]);

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'https://www.90cinema.com/' },
      { '@type': 'ListItem', position: 2, name: currentCategory?.name || '', item: `https://www.90cinema.com/category/${currentSlug}` },
    ],
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }}
      />
      <Navbar />
      <main className="pt-16">
        <section className="py-10 md:py-14 px-4 md:px-6 bg-background-50">
          <div className="max-w-screen-2xl mx-auto">
            <nav className="flex items-center gap-2 text-sm text-foreground-500 mb-6" aria-label="面包屑导航">
              <Link to="/" className="hover:text-primary-500 transition-colors">首页</Link>
              <i className="ri-arrow-right-s-line text-xs"></i>
              <span className="text-foreground-900 font-medium">{currentCategory?.name}</span>
            </nav>

            <h1 className="text-2xl md:text-4xl font-heading text-foreground-900 mb-2">
              {currentCategory?.name}
            </h1>
            <p className="text-sm text-foreground-600 mb-8">
              共 {filtered.length} 部{currentCategory?.name}资源，高清流畅在线观看
            </p>

            <div className="flex items-center gap-2 mb-8 overflow-x-auto pb-2">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className={`px-4 py-2 text-sm rounded-full whitespace-nowrap transition-all ${
                    currentSlug === cat.slug
                      ? 'bg-primary-500 text-white'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200 border border-background-200'
                  }`}
                >
                  <i className={`${cat.icon} mr-1`}></i>
                  {cat.name}
                </Link>
              ))}
            </div>

            <div className="flex items-center gap-2 mb-8 flex-wrap">
              {allSubs.map((sub) => (
                <button
                  key={sub}
                  onClick={() => setActiveSub(sub)}
                  className={`px-3 py-1.5 text-xs rounded-full transition-all whitespace-nowrap ${
                    activeSub === sub
                      ? 'bg-accent-500 text-white'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200 border border-background-200'
                  }`}
                >
                  {sub}
                </button>
              ))}
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4 md:gap-5">
              {filtered.map((movie) => (
                <Link
                  key={movie.id}
                  to={`/article/${movie.id}`}
                  className="group"
                >
                  <div className="relative rounded-lg overflow-hidden aspect-[2/3] mb-3">
                    <img
                      src={movie.poster}
                      alt={movie.title}
                      loading="lazy"
                      className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-110"
                    />
                    <div className="absolute top-2 left-2">
                      <span className="px-2 py-0.5 bg-accent-500 text-white text-[10px] font-medium rounded">
                        {movie.quality[0]}
                      </span>
                    </div>
                    <div className="absolute top-2 right-2">
                      <span className="px-1.5 py-0.5 bg-black/60 text-accent-400 text-[10px] rounded">
                        {movie.rating}
                      </span>
                    </div>
                  </div>
                  <h3 className="text-sm font-medium text-foreground-900 line-clamp-1 group-hover:text-primary-500 transition-colors">
                    {movie.title}
                  </h3>
                  <p className="text-xs text-foreground-500 mt-1">{movie.subCategory} · {movie.year}</p>
                </Link>
              ))}
            </div>

            {filtered.length === 0 && (
              <div className="text-center py-20">
                <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center bg-background-100 rounded-full">
                  <i className="ri-inbox-line text-2xl text-foreground-400"></i>
                </div>
                <p className="text-foreground-500">暂无该分类的影片</p>
              </div>
            )}
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}